// ============================================
// Reports Page — Driver & PA Activity Reports
// With PDF Export & Filtering
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import DispatcherLayout from '../../components/dispatcher/DispatcherLayout';
import adminService from '../../services/adminService';
import dispatcherService from '../../services/dispatcherService';
import socketService from '../../services/socket';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { groupRoutes } from '../../utils/routeUtils';

// ── Icons ────────────────────────────────────
const icons = {
    search: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>,
    pdf: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>,
    driver: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" /></svg>,
    pa: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>,
    calendar: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>,
    expand: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>,
    collapse: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" /></svg>,
};

const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatTime = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const statusColors = {
    completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    morning_completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    afternoon_completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    started: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    cancelled: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
    assigned: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
    unassigned: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
    flagged: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
};

// Resolve the human-readable status label + style for a task
const resolveTaskStatus = (assignStatus, completionStatus) => {
    if (assignStatus === 'completed' && completionStatus === 'issue') {
        return { label: 'FLAGGED', colorKey: 'flagged' };
    }
    const labelMap = {
        morning_completed: 'MORNING DONE',
        afternoon_completed: 'AFTERNOON DONE',
    };
    return { label: labelMap[assignStatus] || assignStatus.replace(/_/g, ' ').toUpperCase(), colorKey: assignStatus };
};

// Decode HTML entities (&amp; → &, &amp;amp; → &, etc.)
const decodeHtml = (str) => {
    if (!str) return str;
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    // Handle double/triple encoded entities
    let decoded = txt.value;
    while (decoded.includes('&amp;')) {
        txt.innerHTML = decoded;
        decoded = txt.value;
    }
    return decoded;
};

const inputClass = "w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 focus:border-indigo-500 dark:focus:border-amber-500 text-slate-900 dark:text-white outline-none transition-all font-medium text-sm";

const Reports = () => {
    const location = useLocation();
    const isDispatcher = location.pathname.startsWith('/dispatcher');
    const Layout = isDispatcher ? DispatcherLayout : AdminLayout;
    
    const [routes, setRoutes] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('drivers'); // 'drivers' | 'pa'
    const [expandedId, setExpandedId] = useState(null);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDriver, setSelectedDriver] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedPA, setSelectedPA] = useState('');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const service = isDispatcher ? dispatcherService : adminService;
            const [routeRes, driverRes, schoolRes, jobRes] = await Promise.all([
                service.getRoutes(),
                service.getDrivers(),
                service.getSchools(),
                service.getJobs(),
            ]);
            setRoutes(groupRoutes(routeRes.data.data.routes || []));
            setDrivers(driverRes.data.data.drivers || []);
            setSchools(schoolRes.data.data.schools || []);
            setJobs(jobRes.data.data.jobs || []);
        } catch (err) { console.error('Failed to load report data:', err); }
        finally { setLoading(false); }
    }, [isDispatcher]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Real-time Updates ────────────────────
    useEffect(() => {
        socketService.on('route:updated', fetchData);
        socketService.on('job:updated', fetchData);
        
        return () => {
            socketService.off('route:updated', fetchData);
            socketService.off('job:updated', fetchData);
        };
    }, [fetchData]);

    // ── Build driver report data ─────────────
    const buildDriverReportData = () => {
        const driverMap = {};

        const ensureDriver = (dId, driverObj = null) => {
            if (!driverMap[dId]) {
                driverMap[dId] = {
                    driver: driverObj || drivers.find(d => String(d._id) === String(dId)) || { _id: dId, name: 'Unknown' },
                    routes: [],
                    jobs: []
                };
            }
        };

        // ── Process ROUTES via assignmentLog ──
        routes.forEach(route => {
            let log = route.assignmentLog || [];
            
            // Fallback for old routes with no assignmentLog
            if (log.length === 0) {
                // Build a synthetic log from completedByDriverId and current driverId
                const syntheticLog = [];
                
                if (route.completedByDriverId) {
                    syntheticLog.push({ driverId: route.completedByDriverId, status: route.status || 'completed' });
                }
                
                if (route.driverId) {
                    const currentDId = typeof route.driverId === 'object' ? route.driverId._id : route.driverId;
                    const completedDId = route.completedByDriverId ? (typeof route.completedByDriverId === 'object' ? route.completedByDriverId._id : route.completedByDriverId) : null;
                    
                    // Only add current driver if different from completedBy
                    if (!completedDId || currentDId !== completedDId) {
                        syntheticLog.push({ driverId: route.driverId, status: route.status === 'started' ? 'started' : 'assigned' });
                    }
                }
                
                log = syntheticLog;
            }

            // Build terminal state per driver from the log
            // IMMUTABLE RULE: Once a driver reaches 'completed'/'started', that status is FROZEN
            // and cannot be overwritten by later 'unassigned' events (which happen when admin reassigns)
            const driverTerminalState = {};
            log.forEach(event => {
                if (!event.driverId) return;
                const dId = typeof event.driverId === 'object' ? event.driverId._id : event.driverId;
                const existing = driverTerminalState[dId];
                
                // If this driver already has a terminal/completed state, do NOT overwrite it
                if (existing && (existing.status === 'completed' || existing.status === 'morning_completed' || existing.status === 'afternoon_completed')) {
                    return; // Freeze — this driver's record is immutable
                }
                // If driver was 'started' and new event is 'unassigned', keep 'started'
                if (existing && existing.status === 'started' && event.status === 'unassigned') {
                    return; // Protect started status from unassign override
                }
                
                driverTerminalState[dId] = { ...event };
            });

            // Force override: ensure completedByDriverId gets 'completed' even if log is missing it
            const isCompletedRoute = route.status === 'completed' || route.status === 'morning_completed' || route.status === 'afternoon_completed';
            if (isCompletedRoute && route.completedByDriverId) {
                const cId = typeof route.completedByDriverId === 'object' ? route.completedByDriverId._id : route.completedByDriverId;
                driverTerminalState[cId] = { driverId: route.completedByDriverId, status: 'completed' };
            }
            
            // Ensure the current driver always appears (even if just 'assigned')
            if (route.driverId) {
                const curId = typeof route.driverId === 'object' ? route.driverId._id : route.driverId;
                if (!driverTerminalState[curId]) {
                    driverTerminalState[curId] = { driverId: route.driverId, status: 'assigned' };
                }
            }
            
            // If route is started by current driver and they don't have a terminal state yet
            if (route.status === 'started' && route.driverId) {
                const cId = typeof route.driverId === 'object' ? route.driverId._id : route.driverId;
                if (!driverTerminalState[cId] || driverTerminalState[cId].status === 'assigned') {
                    driverTerminalState[cId] = { driverId: route.driverId, status: 'started' };
                }
            }
            
            // Remove drivers whose only state is 'unassigned' (they were removed and never completed)
            Object.keys(driverTerminalState).forEach(dId => {
                if (driverTerminalState[dId].status === 'unassigned') {
                    delete driverTerminalState[dId];
                }
            });

            Object.keys(driverTerminalState).forEach(dId => {
                const event = driverTerminalState[dId];
                ensureDriver(dId, typeof event.driverId === 'object' ? event.driverId : null);
                
                let assignStatus = event.status;
                if (route.isMerged && event.status === 'completed' && route.status !== 'completed') {
                    assignStatus = route.status; // e.g., 'morning_completed'
                }

                const routeForDriver = {
                    ...route,
                    _assignmentStatus: assignStatus, // Use separate field to not confuse with route status
                };
                
                driverMap[dId].routes.push(routeForDriver);
            });
        });

        // ── Process PRIVATE JOBS via assignmentLog ──
        jobs.forEach(job => {
            let log = job.assignmentLog || [];
            
            // Fallback for old jobs with no assignmentLog
            if (log.length === 0) {
                const syntheticLog = [];
                if (job.completedByDriverId) {
                    syntheticLog.push({ driverId: job.completedByDriverId, status: job.status || 'completed' });
                }
                if (job.driverId) {
                    const currentDId = typeof job.driverId === 'object' ? job.driverId._id : job.driverId;
                    const completedDId = job.completedByDriverId ? (typeof job.completedByDriverId === 'object' ? job.completedByDriverId._id : job.completedByDriverId) : null;
                    if (!completedDId || currentDId !== completedDId) {
                        syntheticLog.push({ driverId: job.driverId, status: job.status === 'started' ? 'started' : (job.status === 'accepted' ? 'assigned' : job.status) });
                    }
                }
                log = syntheticLog;
            }

            // IMMUTABLE RULE: Same as routes — completed status is frozen
            const driverTerminalState = {};
            log.forEach(event => {
                if (!event.driverId) return;
                const dId = typeof event.driverId === 'object' ? event.driverId._id : event.driverId;
                const existing = driverTerminalState[dId];
                
                if (existing && (existing.status === 'completed')) {
                    return; // Freeze completed
                }
                if (existing && existing.status === 'started' && event.status === 'unassigned') {
                    return; // Protect started
                }
                
                driverTerminalState[dId] = { ...event };
            });

            // Force override: ensure completedByDriverId gets 'completed'
            if (job.status === 'completed' && job.completedByDriverId) {
                const cId = typeof job.completedByDriverId === 'object' ? job.completedByDriverId._id : job.completedByDriverId;
                driverTerminalState[cId] = { driverId: job.completedByDriverId, status: 'completed' };
            }
            
            // Ensure current driver always appears
            if (job.driverId) {
                const curId = typeof job.driverId === 'object' ? job.driverId._id : job.driverId;
                if (!driverTerminalState[curId]) {
                    driverTerminalState[curId] = { driverId: job.driverId, status: 'assigned' };
                }
            }
            
            // If job is started by current driver
            if (job.status === 'started' && job.driverId) {
                const cId = typeof job.driverId === 'object' ? job.driverId._id : job.driverId;
                if (!driverTerminalState[cId] || driverTerminalState[cId].status === 'assigned') {
                    driverTerminalState[cId] = { driverId: job.driverId, status: 'started' };
                }
            }
            
            // Remove drivers whose only state is 'unassigned'
            Object.keys(driverTerminalState).forEach(dId => {
                if (driverTerminalState[dId].status === 'unassigned') {
                    delete driverTerminalState[dId];
                }
            });

            Object.keys(driverTerminalState).forEach(dId => {
                const event = driverTerminalState[dId];
                ensureDriver(dId, typeof event.driverId === 'object' ? event.driverId : null);
                
                const jobForDriver = {
                    ...job,
                    _assignmentStatus: event.status,
                    _isPrivateJob: true,
                };
                
                driverMap[dId].jobs.push(jobForDriver);
            });
        });

        // Also add all drivers (verified or not) who have no routes/jobs yet,
        // so they appear in the dropdown — past completed work is already captured via assignmentLog above
        drivers.forEach(d => {
            if (d.role === 'driver' && !driverMap[d._id]) {
                // Only include if verified (all docs approved) so unverified drivers
                // with no task history don't pollute the list
                const docKeys = ['drivingLicense', 'insurance', 'mot', 'rightToWork', 'councilPaperLicense', 'vehicleCouncilPaperLicense', 'dbsCertificate'];
                const allApproved = docKeys.every(k => d.docs?.[k]?.status === 'approved');
                if (allApproved) {
                    driverMap[d._id] = { driver: d, routes: [], jobs: [] };
                }
            }
        });

        let data = Object.values(driverMap);

        // Apply filters
        if (selectedDriver) {
            data = data.filter(d => d.driver?._id === selectedDriver);
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            data = data.filter(d => 
                d.driver?.name?.toLowerCase().includes(term) ||
                d.driver?.phone?.toLowerCase().includes(term) ||
                d.driver?.email?.toLowerCase().includes(term)
            );
        }

        return data;
    };

    // ── Build PA report data ─────────────────
    const buildPAReportData = () => {
        // Collect PA info from schools and children across routes
        const paList = [];

        // School-level PAs
        schools.forEach(school => {
            if (school.paAssigned && school.paDetails?.name) {
                const schoolRoutes = routes.filter(r => {
                    const sid = r.schoolId?._id || r.schoolId;
                    return String(sid) === String(school._id);
                });
                paList.push({
                    type: 'School',
                    paName: school.paDetails.name,
                    paPhone: school.paDetails.phone || '',
                    paNotes: school.paDetails.notes || '',
                    linkedTo: decodeHtml(school.name),
                    linkedId: school._id,
                    routes: schoolRoutes,
                });
            }
        });

        // Children-level PAs
        routes.forEach(route => {
            (route.children || []).forEach(child => {
                if (child.paAssigned && child.paDetails?.name) {
                    // Avoid duplicates
                    const exists = paList.find(p => p.paName === child.paDetails.name && p.linkedTo === decodeHtml(child.name));
                    if (!exists) {
                        paList.push({
                            type: 'Child',
                            paName: child.paDetails.name,
                            paPhone: child.paDetails.phone || '',
                            paNotes: child.paDetails.notes || '',
                            linkedTo: decodeHtml(child.name),
                            linkedId: child._id,
                            routes: [route],
                        });
                    } else {
                        // Append route to existing PA entry if not already present
                        const existingRoute = exists.routes.find(r => String(r._id) === String(route._id));
                        if (!existingRoute) {
                            exists.routes.push(route);
                        }
                    }
                }
            });
        });

        let data = paList;
        if (selectedPA) {
            data = data.filter(p => p.paName === selectedPA);
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            data = data.filter(p => 
                p.paName.toLowerCase().includes(term) ||
                p.linkedTo.toLowerCase().includes(term) ||
                p.paPhone.toLowerCase().includes(term)
            );
        }

        return data;
    };

    // ── Filter tasks (routes+jobs) by date/status ─────────
    const filterTasks = (tasksList) => {
        let filtered = [...tasksList];
        if (statusFilter) {
            filtered = filtered.filter(r => (r._assignmentStatus || r.status) === statusFilter);
        }
        if (dateFrom) {
            const from = new Date(dateFrom);
            filtered = filtered.filter(r => {
                const d = r.actualStartTime || r.scheduledDate || r.createdAt;
                return d && new Date(d) >= from;
            });
        }
        if (dateTo) {
            const to = new Date(dateTo + 'T23:59:59');
            filtered = filtered.filter(r => {
                const d = r.actualStartTime || r.scheduledDate || r.createdAt;
                return d && new Date(d) <= to;
            });
        }
        return filtered;
    };

    // ── PDF Generation ───────────────────────
    const generatePDF = () => {
        const doc = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const now = new Date().toLocaleString('en-GB');

        // ── Premium Header ──
        doc.setFillColor(15, 23, 42);
        doc.rect(0, 0, pageWidth, 32, 'F');
        doc.setFillColor(99, 102, 241);
        doc.rect(0, 0, 5, 32, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(17);
        doc.setFont('helvetica', 'bold');
        doc.text('IdaCab', 12, 13);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text('Transport Management System', 12, 21);
        const reportTypeLabel = activeTab === 'drivers' ? 'Driver Activity Report' : 'PA Activity Report';
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(199, 210, 254);
        doc.text(reportTypeLabel, pageWidth - 12, 13, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(`Generated: ${now}`, pageWidth - 12, 21, { align: 'right' });

        // ── Filters bar ──
        const filters = [];
        if (selectedDriver) {
            const dName = drivers.find(d => d._id === selectedDriver)?.name || selectedDriver;
            filters.push(`Driver: ${dName}`);
        }
        if (dateFrom) filters.push(`From: ${formatDate(dateFrom)}`);
        if (dateTo) filters.push(`To: ${formatDate(dateTo)}`);
        if (statusFilter) filters.push(`Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`);
        if (filters.length > 0) {
            doc.setFillColor(30, 41, 59);
            doc.rect(0, 32, pageWidth, 9, 'F');
            doc.setFontSize(7);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(148, 163, 184);
            doc.text(`Filters: ${filters.join('  ·  ')}`, 12, 37.5);
        }

        let startY = filters.length > 0 ? 47 : 38;

        if (activeTab === 'drivers') {
            const reportData = buildDriverReportData();
            
            // Build flat table rows
            const allRows = [];
            reportData.forEach((entry) => {
                const allTasks = [...filterTasks(entry.routes), ...filterTasks(entry.jobs || [])];
                if (allTasks.length === 0) {
                    allRows.push([
                        entry.driver?.name || 'Unknown',
                        entry.driver?.phone || 'N/A',
                        entry.driver?.email || 'N/A',
                        '', 'No tasks match filters', '', '', '', '', '', '', '', '',
                    ]);
                } else {
                    allTasks.forEach(r => {
                        const isJob = r._isPrivateJob;
                        const assignStatus = r._assignmentStatus || r.status || 'pending';
                        const { label: statusLabel } = resolveTaskStatus(assignStatus, r.completionStatus);
                        allRows.push([
                            entry.driver?.name || 'Unknown',
                            entry.driver?.phone || 'N/A',
                            entry.driver?.email || 'N/A',
                            isJob ? 'PRIVATE JOB' : 'SCHOOL ROUTE',
                            decodeHtml(r.name),
                            isJob ? (r.pickupAddress || '—') : (decodeHtml(r.schoolId?.name) || '—'),
                            isJob ? 'PRIVATE' : (r.type?.replace('_', ' & ').toUpperCase() || '—'),
                            statusLabel,
                            formatDate(r.actualStartTime || r.scheduledDate || r.createdAt),
                            formatTime(r.actualStartTime) || r.scheduledTime || '—',
                            formatDate(r.actualEndTime),
                            formatTime(r.actualEndTime) || '—',
                            isJob ? (r.price ? `£${Number(r.price).toFixed(2)}` : '—') : (r.schoolId?.dailyRate != null ? `£${r.schoolId.dailyRate.toFixed(2)}` : '—'),
                            r.completionNote || '—',
                        ]);
                    });
                }
            });

            if (allRows.length > 0) {
                autoTable(doc, {
                    startY: startY,
                    head: [['Driver', 'Phone', 'Email', 'Type', 'Task / Client', 'Location', 'Shift', 'Status', 'Date', 'Start', 'End', 'Finish', 'Fee', 'Notes']],
                    body: allRows,
                    theme: 'plain',
                    headStyles: { 
                        fillColor: [15, 23, 42], 
                        textColor: [199, 210, 254], 
                        fontSize: 6, 
                        fontStyle: 'bold', 
                        halign: 'center',
                        cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
                    },
                    bodyStyles: { fontSize: 6.5, cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 }, textColor: [30, 41, 59] },
                    alternateRowStyles: { fillColor: [248, 250, 252] },
                    rowPageBreak: 'avoid',
                    columnStyles: {
                        0: { cellWidth: 20, fontStyle: 'bold' },
                        1: { cellWidth: 17 },
                        2: { cellWidth: 26 },
                        3: { halign: 'center', cellWidth: 15, fontStyle: 'bold' },
                        7: { halign: 'center', fontStyle: 'bold' },
                        8: { halign: 'center', cellWidth: 14 },
                        9: { halign: 'center', cellWidth: 11 },
                        10: { halign: 'center', cellWidth: 14 },
                        11: { halign: 'center', cellWidth: 11 },
                        12: { halign: 'right', cellWidth: 14, fontStyle: 'bold' },
                    },
                    margin: { left: 8, right: 8 },
                });
                startY = doc.lastAutoTable.finalY + 10;
            }

            // ── Premium Summary Box ──
            if (startY > doc.internal.pageSize.getHeight() - 50) {
                doc.addPage();
                startY = 14;
            }

            const totalRoutes = reportData.reduce((sum, d) => sum + filterTasks(d.routes).length, 0);
            const totalJobs = reportData.reduce((sum, d) => sum + filterTasks(d.jobs || []).length, 0);
            const completedTasks = reportData.reduce((sum, d) => sum + [...filterTasks(d.routes), ...filterTasks(d.jobs || [])].filter(r => (r._assignmentStatus || r.status) === 'completed').length, 0);
            const totalRate = reportData.reduce((sum, d) => sum + filterTasks(d.routes).reduce((s, r) => s + (r.schoolId?.dailyRate || 0), 0) + filterTasks(d.jobs || []).reduce((s, j) => s + (j.price || 0), 0), 0);

            const summaryH = 30;
            const summaryW = pageWidth - 16;
            doc.setFillColor(15, 23, 42);
            doc.roundedRect(8, startY, summaryW, summaryH, 2, 2, 'F');
            doc.setFillColor(99, 102, 241);
            doc.roundedRect(8, startY, 3, summaryH, 1, 1, 'F');
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(100, 116, 139);
            doc.text('REPORT SUMMARY', 16, startY + 8);
            const summaryStats = [
                { label: 'Drivers', val: String(reportData.length) },
                { label: 'School Routes', val: String(totalRoutes) },
                { label: 'Private Jobs', val: String(totalJobs) },
                { label: 'Completed', val: String(completedTasks) },
                { label: 'Total Earnings', val: `\u00a3${totalRate.toFixed(2)}` },
            ];
            const colW = summaryW / summaryStats.length;
            summaryStats.forEach((s, i) => {
                const x = 8 + (i * colW) + colW / 2;
                doc.setFontSize(6);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(100, 116, 139);
                doc.text(s.label.toUpperCase(), x, startY + 18, { align: 'center' });
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(255, 255, 255);
                doc.text(s.val, x, startY + 26, { align: 'center' });
            });
            startY += summaryH + 6;

        } else {
            // PA Report
            const paData = buildPAReportData();
            
            if (paData.length > 0) {
                const tableData = paData.map(p => ([
                    p.paName,
                    p.paPhone || '—',
                    p.type,
                    decodeHtml(p.linkedTo),
                    p.routes.length.toString(),
                    p.routes.map(r => decodeHtml(r.name)).join(', ') || '—',
                    p.paNotes || '—',
                ]));

                autoTable(doc, {
                    startY: startY,
                    head: [['PA Name', 'Phone', 'Assigned To', 'Linked Entity', 'Routes', 'Route Numbers', 'Notes']],
                    body: tableData,
                    theme: 'striped',
                    headStyles: { fillColor: [139, 92, 246], fontSize: 8, fontStyle: 'bold', halign: 'center' },
                    bodyStyles: { fontSize: 8, cellPadding: 3 },
                    alternateRowStyles: { fillColor: [248, 250, 252] },
                    margin: { left: 14, right: 14 },
                });
                startY = doc.lastAutoTable.finalY + 10;
            }

            doc.setFillColor(241, 245, 249);
            doc.rect(14, startY, pageWidth - 28, 18, 'F');
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text(`SUMMARY — Total PAs: ${buildPAReportData().length}`, 20, startY + 11);
        }

        // ── Premium Footer on all pages ──
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const h = doc.internal.pageSize.getHeight();
            doc.setFillColor(15, 23, 42);
            doc.rect(0, h - 10, pageWidth, 10, 'F');
            doc.setFillColor(99, 102, 241);
            doc.rect(0, h - 10, 4, 10, 'F');
            doc.setFontSize(6.5);
            doc.setTextColor(100, 116, 139);
            doc.setFont('helvetica', 'normal');
            doc.text('IdaCab Transport Management System  ·  Confidential', 10, h - 3.5);
            doc.setTextColor(199, 210, 254);
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - 10, h - 3.5, { align: 'right' });
        }

        const fileName = `IdaCab_${activeTab === 'drivers' ? 'Driver' : 'PA'}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
    };

    // ── CSV (Excel) Generation ────────────────
    const generateCSV = () => {
        let csvContent = "";
        const now = new Date().toLocaleString('en-GB');
        
        if (activeTab === 'drivers') {
            csvContent += "Driver,Phone,Email,Type,Task Name,School/Pickup,Shift,Status,Start Date,Start Time,End Date,End Time,Rate,Outcome Notes\n";
            const reportData = buildDriverReportData();
            
            reportData.forEach((entry) => {
                const allTasks = [...filterTasks(entry.routes), ...filterTasks(entry.jobs || [])];
                if (allTasks.length === 0) {
                    csvContent += `"${entry.driver?.name || 'Unknown'}","${entry.driver?.phone || 'N/A'}","${entry.driver?.email || 'N/A'}","","No tasks match filters","","","","","","","",""\n`;
                } else {
                    allTasks.forEach(r => {
                        const isJob = r._isPrivateJob;
                        const assignStatus = r._assignmentStatus || r.status || 'pending';
                        const { label: statusLabel } = resolveTaskStatus(assignStatus, r.completionStatus);
                        csvContent += `"${entry.driver?.name || 'Unknown'}",`;
                        csvContent += `"${entry.driver?.phone || 'N/A'}",`;
                        csvContent += `"${entry.driver?.email || 'N/A'}",`;
                        csvContent += `"${isJob ? 'PRIVATE JOB' : 'SCHOOL ROUTE'}",`;
                        csvContent += `"${decodeHtml(r.name)}",`;
                        csvContent += `"${isJob ? (r.pickupAddress || '—') : (decodeHtml(r.schoolId?.name) || '—')}",`;
                        csvContent += `"${isJob ? 'PRIVATE' : (r.type?.replace('_', ' & ').toUpperCase() || '—')}",`;
                        csvContent += `"${statusLabel}",`;
                        csvContent += `"${formatDate(r.actualStartTime || r.scheduledDate || r.createdAt)}",`;
                        csvContent += `"${formatTime(r.actualStartTime) || r.scheduledTime || '—'}",`;
                        csvContent += `"${formatDate(r.actualEndTime)}",`;
                        csvContent += `"${formatTime(r.actualEndTime) || '—'}",`;
                        csvContent += `"${isJob ? (r.price ? '£' + Number(r.price).toFixed(2) : '—') : (r.schoolId?.dailyRate != null ? '£' + r.schoolId.dailyRate.toFixed(2) : '—')}",`;
                        csvContent += `"${r.completionNote || '—'}"\n`;
                    });
                }
            });
        } else {
            csvContent += "PA Name,Phone,Assigned To,Linked Entity,Routes Count,Route Numbers,Notes\n";
            const paData = buildPAReportData();
            
            paData.forEach(p => {
                csvContent += `"${p.paName}","${p.paPhone || '—'}","${p.type}","${decodeHtml(p.linkedTo)}","${p.routes.length}",`;
                csvContent += `"${p.routes.map(r => decodeHtml(r.name)).join(', ') || '—'}","${p.paNotes || '—'}"\n`;
            });
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `IdaCab_${activeTab === 'drivers' ? 'Driver' : 'PA'}_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const driverReportData = buildDriverReportData();
    const paReportData = buildPAReportData();
    
    // Build driver dropdown from ALL historical drivers (from assignmentLog, completedByDriverId, current driverId)
    const activeDriverFiltered = (() => {
        const activeIds = new Set();
        
        const extractId = (val) => {
            if (!val) return null;
            return typeof val === 'object' ? val._id : val;
        };

        // From routes
        routes.forEach(r => {
            (r.assignmentLog || []).forEach(evt => activeIds.add(extractId(evt.driverId)));
            activeIds.add(extractId(r.completedByDriverId));
            activeIds.add(extractId(r.driverId));
        });
        
        // From jobs
        jobs.forEach(j => {
            (j.assignmentLog || []).forEach(evt => activeIds.add(extractId(evt.driverId)));
            activeIds.add(extractId(j.completedByDriverId));
            activeIds.add(extractId(j.driverId));
        });
        
        const driverSet = new Map();
        
        // Map collected string IDs to full driver objects
        activeIds.forEach(id => {
            if (id) {
                const d = drivers.find(drv => String(drv._id) === String(id));
                if (d) driverSet.set(String(d._id), d);
            }
        });
        
        // Also add all drivers who appear anywhere in the drivers list (past or present)
        // so the dropdown shows historical drivers regardless of current doc status
        drivers.forEach(d => {
            if (d.role === 'driver' && !driverSet.has(String(d._id))) {
                driverSet.set(String(d._id), d);
            }
        });
        
        return Array.from(driverSet.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    })();

    return (
        <Layout title="Reports" subtitle="Driver & PA activity reports with PDF export" hideSearch>
            <div className="max-w-[1600px] mx-auto space-y-6">

                {/* Tab Selector + PDF Export */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl p-4 rounded-2xl border border-white/60 dark:border-white/5 shadow-sm">
                    <div className="flex gap-2">
                        <button onClick={() => { setActiveTab('drivers'); setExpandedId(null); }}
                            className={`flex items-center gap-2 px-5 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'drivers' ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/30' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>
                            {icons.driver} Driver Reports
                        </button>
                        <button onClick={() => { setActiveTab('pa'); setExpandedId(null); }}
                            className={`flex items-center gap-2 px-5 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'pa' ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/30' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>
                            {icons.pa} PA Reports
                        </button>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={generateCSV} disabled={loading}
                            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-[11px] font-black uppercase tracking-widest hover:from-emerald-400 hover:to-teal-500 shadow-xl shadow-emerald-500/25 active:scale-95 transition-all disabled:opacity-50">
                            {icons.calendar} Export Excel
                        </button>
                        <button onClick={generatePDF} disabled={loading}
                            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-600 to-pink-600 text-white text-[11px] font-black uppercase tracking-widest hover:from-rose-500 hover:to-pink-500 shadow-xl shadow-rose-500/25 active:scale-95 transition-all disabled:opacity-50">
                            {icons.pdf} Export PDF
                        </button>
                    </div>
                </div>

                {/* Filters Bar */}
                <div className="bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl p-5 rounded-2xl border border-white/60 dark:border-white/5 shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">{icons.search}</div>
                            <input type="text" placeholder={activeTab === 'drivers' ? 'Search drivers...' : 'Search PA...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-sm text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 font-medium" />
                        </div>

                        {/* Driver Filter */}
                        {activeTab === 'drivers' && (
                            <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)} className={inputClass}>
                                <option value="">All Drivers</option>
                                {activeDriverFiltered.map(d => (
                                    <option key={d._id} value={d._id}>{d.name}</option>
                                ))}
                            </select>
                        )}

                        {/* PA Filter */}
                        {activeTab === 'pa' && (
                            <select value={selectedPA} onChange={(e) => setSelectedPA(e.target.value)} className={inputClass}>
                                <option value="">All PAs</option>
                                {(() => {
                                    const allPAs = [];
                                    schools.forEach(s => { if (s.paAssigned && s.paDetails?.name && !allPAs.includes(s.paDetails.name)) allPAs.push(s.paDetails.name); });
                                    routes.forEach(r => (r.children || []).forEach(c => { if (c.paAssigned && c.paDetails?.name && !allPAs.includes(c.paDetails.name)) allPAs.push(c.paDetails.name); }));
                                    return allPAs.map(name => <option key={name} value={name}>{name}</option>);
                                })()}
                            </select>
                        )}

                        {/* Date From */}
                        <div className="relative">
                            <label className="absolute -top-2 left-3 text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest bg-white dark:bg-slate-900 px-1 z-10">From</label>
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputClass} />
                        </div>

                        {/* Date To */}
                        <div className="relative">
                            <label className="absolute -top-2 left-3 text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest bg-white dark:bg-slate-900 px-1 z-10">To</label>
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputClass} />
                        </div>

                        {/* Status Filter */}
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputClass}>
                            <option value="">All Statuses</option>
                            <option value="completed">Completed</option>
                            <option value="started">Started</option>
                            <option value="assigned">Assigned</option>
                            <option value="pending">Pending</option>
                            <option value="unassigned">Unassigned</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                </div>

                {/* Content */}
                <div className="bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/5 overflow-hidden shadow-sm">
                    {loading ? (
                        <div className="flex items-center justify-center py-32"><div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" /></div>
                    ) : activeTab === 'drivers' ? (
                        /* ═════════════════════════════════════════
                           DRIVER REPORTS
                           ═════════════════════════════════════════ */
                        <div>
                            {driverReportData.length === 0 ? (
                                <div className="text-center py-24">
                                    <div className="w-20 h-20 mx-auto rounded-[2rem] bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 flex items-center justify-center mb-6">{icons.driver}</div>
                                    <p className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-widest">No Drivers Found</p>
                                    <p className="text-sm text-slate-500 mt-2">Try adjusting your search or filters.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto overflow-y-auto max-h-[600px] sidebar-scroll">
                                    <table className="w-full">
                                        <thead className="sticky top-0 z-10">
                                            <tr className="bg-slate-100/95 dark:bg-slate-800/95 backdrop-blur-sm">
                                                <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Driver Name</th>
                                                <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Phone</th>
                                                <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Email</th>
                                                <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Type</th>
                                                <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Task Name</th>
                                                <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">School</th>
                                                <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Shift</th>
                                                <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Status</th>
                                                <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Start Date</th>
                                                <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Start Time</th>
                                                <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">End Date</th>
                                                <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">End Time</th>
                                                <th className="px-4 py-3 text-right text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Rate</th>
                                                <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Outcome Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                                            {driverReportData.flatMap((entry) => {
                                                const d = entry.driver;
                                                const allTasks = [...filterTasks(entry.routes), ...filterTasks(entry.jobs || [])];
                                                if (allTasks.length === 0) {
                                                    return [(
                                                        <tr key={d?._id || 'unknown'} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                                            <td className="px-4 py-3 text-[11px] font-bold text-slate-800 dark:text-slate-200 whitespace-nowrap">{d?.name || 'Unknown'}</td>
                                                            <td className="px-4 py-3 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 font-mono whitespace-nowrap">{d?.phone || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-[11px] font-bold text-slate-600 dark:text-slate-300 max-w-[180px] truncate">{d?.email || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-[11px] text-slate-400 italic" colSpan={10}>No tasks match filters</td>
                                                        </tr>
                                                    )];
                                                }
                                                return allTasks.map((r, ri) => {
                                                    const isJob = r._isPrivateJob;
                                                    const assignStatus = r._assignmentStatus || r.status || 'pending';
                                                    return (
                                                    <tr key={`${d?._id}-${r._id}-${ri}`} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                                        <td className="px-4 py-3 text-[11px] font-bold text-slate-800 dark:text-slate-200 whitespace-nowrap">{d?.name || 'Unknown'}</td>
                                                        <td className="px-4 py-3 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 font-mono whitespace-nowrap">{d?.phone || 'N/A'}</td>
                                                        <td className="px-4 py-3 text-[11px] font-bold text-slate-600 dark:text-slate-300 max-w-[180px] truncate">{d?.email || 'N/A'}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <div className="flex flex-col gap-1 items-center justify-center">
                                                                {isJob ? (
                                                                    <>
                                                                        <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20">PRIVATE</span>
                                                                        <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20">JOB</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20">SCHOOL</span>
                                                                        <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20">ROUTE</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-[11px] font-bold text-slate-800 dark:text-slate-200 whitespace-nowrap">{decodeHtml(r.name)}</td>
                                                        <td className="px-4 py-3 text-[11px] font-bold text-slate-600 dark:text-slate-300 max-w-[120px] truncate">{isJob ? (r.pickupAddress || '—') : (decodeHtml(r.schoolId?.name) || '—')}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            {isJob ? (
                                                                <span className="px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest border bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20">PRIVATE</span>
                                                            ) : (
                                                            <div className="flex flex-col gap-1 items-center justify-center">
                                                                {r.type === 'morning_afternoon' ? (
                                                                    <>
                                                                        <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">MORNING</span>
                                                                        <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20">AFTERNOON</span>
                                                                    </>
                                                                ) : (
                                                                    <span className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest border ${r.type === 'morning' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' : r.type === 'afternoon' ? 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20' : 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20'}`}>
                                                                        {r.type?.replace('_', ' & ').toUpperCase()}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {(() => {
                                                                const { label, colorKey } = resolveTaskStatus(assignStatus, r.completionStatus);
                                                                if (assignStatus === 'morning_completed' || assignStatus === 'afternoon_completed') {
                                                                    return (
                                                                        <div className="flex flex-col gap-1 items-center justify-center">
                                                                            <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border ${assignStatus === 'morning_completed' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' : 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20'}`}>
                                                                                {assignStatus.split('_')[0]}
                                                                            </span>
                                                                            <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">DONE</span>
                                                                        </div>
                                                                    );
                                                                }
                                                                return <span className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest border whitespace-nowrap ${statusColors[colorKey] || statusColors.pending}`}>{label}</span>;
                                                            })()}
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-[10px] font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatDate(r.actualStartTime || r.scheduledDate || r.createdAt)}</td>
                                                        <td className="px-4 py-3 text-center text-[10px] font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatTime(r.actualStartTime) || r.scheduledTime || '—'}</td>
                                                        <td className="px-4 py-3 text-center text-[10px] font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatDate(r.actualEndTime)}</td>
                                                        <td className="px-4 py-3 text-center text-[10px] font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatTime(r.actualEndTime) || '—'}</td>
                                                        <td className="px-4 py-3 text-right text-[10px] font-black text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{isJob ? (r.price ? `£${Number(r.price).toFixed(2)}` : '—') : (r.schoolId?.dailyRate != null ? `£${r.schoolId.dailyRate.toFixed(2)}` : '—')}</td>
                                                        <td className="px-4 py-3 text-left text-[10px] text-slate-500 dark:text-slate-400 italic max-w-[160px]">{r.completionNote || <span className="text-slate-300 dark:text-slate-600">—</span>}</td>
                                                    </tr>
                                                    );
                                                });
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {/* Summary Bar */}
                            {driverReportData.length > 0 && (
                                <div className="px-6 py-4 bg-slate-50/80 dark:bg-slate-800/40 border-t border-slate-200 dark:border-slate-700/60 flex flex-wrap gap-6">
                                    <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Total Drivers</span><span className="text-sm font-black text-slate-900 dark:text-white">{driverReportData.length}</span></div>
                                    <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">School Routes</span><span className="text-sm font-black text-slate-900 dark:text-white">{driverReportData.reduce((sum, d) => sum + filterTasks(d.routes).length, 0)}</span></div>
                                    <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Private Jobs</span><span className="text-sm font-black text-cyan-600 dark:text-cyan-400">{driverReportData.reduce((sum, d) => sum + filterTasks(d.jobs || []).length, 0)}</span></div>
                                    <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Completed</span><span className="text-sm font-black text-emerald-600 dark:text-emerald-400">{driverReportData.reduce((sum, d) => sum + [...filterTasks(d.routes), ...filterTasks(d.jobs || [])].filter(r => (r._assignmentStatus || r.status) === 'completed').length, 0)}</span></div>
                                    <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Total Daily Rate</span><span className="text-sm font-black text-emerald-600 dark:text-emerald-400">£{driverReportData.reduce((sum, d) => sum + filterTasks(d.routes).reduce((s, r) => s + (r.schoolId?.dailyRate || 0), 0) + filterTasks(d.jobs || []).reduce((s, j) => s + (j.price || 0), 0), 0).toFixed(2)}</span></div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* ═════════════════════════════════════════
                           PA REPORTS
                           ═════════════════════════════════════════ */
                        <div className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {paReportData.length === 0 ? (
                                <div className="text-center py-24">
                                    <div className="w-20 h-20 mx-auto rounded-[2rem] bg-violet-500/10 text-violet-500 dark:text-violet-400 flex items-center justify-center mb-6">{icons.pa}</div>
                                    <p className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-widest">No PA Records Found</p>
                                    <p className="text-sm text-slate-500 mt-2">PA details are added when assigning PAs to schools or children.</p>
                                </div>
                            ) : paReportData.map((pa, idx) => {
                                const isExpanded = expandedId === `pa-${idx}`;
                                const paRoutes = filterTasks(pa.routes);
                                
                                return (
                                    <div key={`pa-${idx}`}>
                                        <div onClick={() => setExpandedId(isExpanded ? null : `pa-${idx}`)}
                                            className={`flex items-center justify-between px-6 py-5 cursor-pointer transition-colors ${isExpanded ? 'bg-violet-50/60 dark:bg-violet-500/5' : 'hover:bg-slate-50/50 dark:hover:bg-slate-800/30'}`}>
                                            <div className="flex items-center gap-4">
                                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-lg shadow-lg ${isExpanded ? 'bg-violet-500 shadow-violet-500/30' : 'bg-gradient-to-br from-violet-400 to-purple-500'}`}>
                                                    {pa.paName.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-slate-900 dark:text-white">{pa.paName}</p>
                                                    <p className="text-[10px] font-bold text-violet-600 dark:text-violet-400 mt-0.5 flex items-center gap-1">
                                                        {pa.type === 'School' ? <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1v1H9V7zm5 0h1v1h-1V7zm-5 4h1v1H9v-1zm5 0h1v1h-1v-1zm-3 4H2v-1h2v1z" /></svg> : <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>} Assigned to {pa.type}: {pa.linkedTo}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="hidden sm:flex items-center gap-3">
                                                    {pa.paPhone && (
                                                        <span className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-bold border border-slate-200 dark:border-slate-700 font-mono flex items-center gap-1">
                                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg> {pa.paPhone}
                                                        </span>
                                                    )}
                                                    <span className="px-3 py-1.5 rounded-xl bg-violet-500/10 text-violet-600 dark:text-violet-400 text-[10px] font-black uppercase tracking-widest border border-violet-500/20">
                                                        {paRoutes.length} Routes
                                                    </span>
                                                </div>
                                                <div className="text-slate-400 transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                                    {icons.expand}
                                                </div>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="px-6 pb-6 bg-slate-50/50 dark:bg-slate-800/20">
                                                {/* PA info */}
                                                <div className="flex flex-wrap gap-4 mb-4 p-4 rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/60">
                                                    <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">PA Name</span><span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">{pa.paName}</span></div>
                                                    <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Phone</span><span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">{pa.paPhone || 'N/A'}</span></div>
                                                    <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Type</span><span className="text-[11px] font-bold text-violet-600 dark:text-violet-400">{pa.type}</span></div>
                                                    <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Linked To</span><span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">{pa.linkedTo}</span></div>
                                                    {pa.paNotes && <div><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Notes</span><span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">{pa.paNotes}</span></div>}
                                                </div>

                                                {paRoutes.length > 0 && (
                                                    <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-700/60">
                                                        <table className="w-full">
                                                            <thead>
                                                                <tr className="bg-slate-100/50 dark:bg-slate-800/50">
                                                                    <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Route</th>
                                                                    <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">School</th>
                                                                    <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Driver</th>
                                                                    <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                                                    <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest">Start</th>
                                                                    <th className="px-4 py-3 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest">End</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                                                                {paRoutes.map(r => (
                                                                    <tr key={r._id} className="hover:bg-white/50 dark:hover:bg-slate-800/30 transition-colors">
                                                                        <td className="px-4 py-3 text-[11px] font-bold text-slate-800 dark:text-slate-200">{decodeHtml(r.name)}</td>
                                                                        <td className="px-4 py-3 text-[11px] font-bold text-slate-600 dark:text-slate-300">{decodeHtml(r.schoolId?.name) || '—'}</td>
                                                                        <td className="px-4 py-3 text-[11px] font-bold text-slate-600 dark:text-slate-300">{r.driverId?.name || 'Unassigned'}</td>
                                                                        <td className="px-4 py-3 text-center">
                                                                            <span className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest border ${statusColors[r.status] || statusColors.pending}`}>{r.status || 'pending'}</span>
                                                                        </td>
                                                                        <td className="px-4 py-3 text-center text-[10px] font-bold text-slate-600 dark:text-slate-300">
                                                                            {formatDate(r.actualStartTime || r.createdAt)} <br/>
                                                                            <span className="text-[9px] text-slate-400">{formatTime(r.actualStartTime) || r.scheduledTime || '—'}</span>
                                                                        </td>
                                                                        <td className="px-4 py-3 text-center text-[10px] font-bold text-slate-600 dark:text-slate-300">
                                                                            {formatDate(r.actualEndTime)} <br/>
                                                                            {formatDate(r.actualEndTime) !== '—' && <span className="text-[9px] text-slate-400">{formatTime(r.actualEndTime) || '—'}</span>}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Reports;
